import React from 'react';
import { useLocation } from 'react-router-dom';

import type { WizardStepProps } from '../wizard';

import type { StepAttachmentsFormData } from './step-clinical-documents';
import { stepAttachments } from './step-clinical-documents';
import type { StepClinicalNotesFormData } from './step-clinical-notes';
import { stepClinicalNotes } from './step-clinical-notes';
import type { StepDetailInformationFormData } from './step-detail-information';
import { stepDetailInformation } from './step-detail-information';
import type { StepNewRequestFormData } from './step-new-request';
import { stepNewRequest } from './step-new-request';
import type { StepPointOfContactFormData } from './step-point-of-contact';
import { stepPointOfContact } from './step-point-of-contact';
import type { StepRenderingProviderFormData } from './step-rendering-provider';
import { stepRenderingProvider } from './step-rendering-provider';
import { stepSummary } from './step-review';
import type { StepVeteranDemographicsFormData } from './step-veteran-demographics';
import { stepVeteranDemographics } from './step-veteran-demographics';

// add new form data interfaces here
export type WizardFormType = StepAttachmentsFormData &
  StepDetailInformationFormData &
  StepVeteranDemographicsFormData &
  StepNewRequestFormData &
  StepPointOfContactFormData &
  StepRenderingProviderFormData &
  StepClinicalNotesFormData;

export const formSteps = [
  stepNewRequest,
  stepVeteranDemographics,
  stepDetailInformation,
  stepRenderingProvider,
  stepAttachments,
  stepClinicalNotes,
  stepPointOfContact,
  stepSummary,
];

interface StepsContextType {
  steps: WizardStepProps[];
  currentStepIndex: number;
  currentStep: WizardStepProps;
  nextStep: WizardStepProps | null;
  previousStep: WizardStepProps | null;
}

const StepsContext = React.createContext<StepsContextType>({
  steps: [],
  get currentStepIndex() {
    return -1;
  },
  get currentStep() {
    return stepNewRequest;
  },
  get nextStep() {
    return null;
  },
  get previousStep() {
    return null;
  },
});

export function StepsProvider({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const splitPath = pathname.split('/');
  const currentStepPath = splitPath.at(-1)!;
  return (
    <StepsContext.Provider
      value={{
        steps: formSteps,
        get currentStepIndex() {
          const currentIndex = formSteps.findIndex(
            (s) => s.path.toLowerCase() === currentStepPath.toLowerCase()
          );
          return currentIndex;
        },
        get currentStep() {
          const currentIndex = formSteps.findIndex(
            (s) => s.path.toLowerCase() === currentStepPath.toLowerCase()
          );
          return currentIndex === -1 ? formSteps[0] : formSteps[currentIndex];
        },
        get nextStep() {
          const currentIndex = formSteps.findIndex(
            (s) => s.path.toLowerCase() === currentStepPath.toLowerCase()
          );
          return currentIndex < formSteps.length - 1
            ? formSteps[currentIndex + 1]
            : null;
        },
        get previousStep() {
          const currentIndex = formSteps.findIndex(
            (s) => s.path.toLowerCase() === currentStepPath.toLowerCase()
          );
          return currentIndex > 0 ? formSteps[currentIndex - 1] : null;
        },
      }}
    >
      {children}
    </StepsContext.Provider>
  );
}

export function useSteps() {
  return React.useContext(StepsContext);
}
